// src/constants.ts
var AssessmentWorkflowState = {
  COLLECTING_EVIDENCE: "COLLECTING_EVIDENCE",
  ANSWERING_QUESTIONS: "ANSWERING_QUESTIONS",
  ANSWERS_SUBMITTED: "ANSWERS_SUBMITTED",
  REVIEWING_REPORT: "REVIEWING_REPORT",
  SHARED: "SHARED",
  FINALIZED: "FINALIZED"
};
var CodeSourceType = {
  CODE_SOURCE_GITHUB: "CODE_SOURCE_GITHUB",
  CODE_SOURCE_UPLOAD: "CODE_SOURCE_UPLOAD"
};
var CodeSourceTypeLabels = {
  [CodeSourceType.CODE_SOURCE_GITHUB]: "GitHub Repository",
  [CodeSourceType.CODE_SOURCE_UPLOAD]: "Code Upload (ZIP)"
};
var CODE_UPLOAD_REPORT_ANNOTATION = "Code was provided to StackVerify via upload rather than repository scan. There are many potential reasons for this -- unsupported code repository, security or privacy concerns -- but it means StackVerify can't report on historical data (commit frequency, contributor count) and can't verify the security of the codebase.";
var CODE_UPLOAD_MAX_SIZE_BYTES = 50 * 1024 * 1024;
var CODE_UPLOAD_CONTENT_TYPE = "application/zip";
var UploadedCodeSourceStatus = {
  PENDING: "pending",
  AWAITING_CONFIRMATION: "awaiting-confirmation",
  UPLOADED: "uploaded",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED_BY_USER: "cancelled-by-user"
};
var AssessmentWorkflowStateLabels = {
  [AssessmentWorkflowState.COLLECTING_EVIDENCE]: "Collect Evidence",
  [AssessmentWorkflowState.ANSWERING_QUESTIONS]: "Answer Questions",
  [AssessmentWorkflowState.ANSWERS_SUBMITTED]: "Answers Submitted",
  [AssessmentWorkflowState.REVIEWING_REPORT]: "Review Report",
  [AssessmentWorkflowState.SHARED]: "Report Shared",
  [AssessmentWorkflowState.FINALIZED]: "Report Finalized"
};
var AssessmentWorkflowStateDescriptions = {
  [AssessmentWorkflowState.COLLECTING_EVIDENCE]: "Upload documents, scan code, and review infrastructure",
  [AssessmentWorkflowState.ANSWERING_QUESTIONS]: "Review and answer generated questions",
  [AssessmentWorkflowState.ANSWERS_SUBMITTED]: "Answers locked \u2014 final analysis in progress",
  [AssessmentWorkflowState.REVIEWING_REPORT]: "Review the assessment report and add management notes",
  [AssessmentWorkflowState.SHARED]: "Report shared with investors \u2014 all content frozen",
  [AssessmentWorkflowState.FINALIZED]: "Assessment finalized \u2014 report generation initiated"
};
var REPORT_FROZEN_STATES = [
  AssessmentWorkflowState.REVIEWING_REPORT,
  AssessmentWorkflowState.SHARED,
  AssessmentWorkflowState.FINALIZED
];
function isReportFrozen(state) {
  return REPORT_FROZEN_STATES.includes(state);
}
function isInvestorVisible(state, sharedWithInvestors) {
  return isReportFrozen(state) && sharedWithInvestors;
}
var RequestStatus = {
  INVITED: "INVITED",
  DECLINED: "DECLINED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  CANCELED: "CANCELED",
  EXPIRED: "EXPIRED"
};
var RequestStatusLabels = {
  [RequestStatus.INVITED]: "Invited",
  [RequestStatus.DECLINED]: "Declined",
  [RequestStatus.IN_PROGRESS]: "In Progress",
  [RequestStatus.COMPLETED]: "Completed",
  [RequestStatus.CANCELED]: "Canceled",
  [RequestStatus.EXPIRED]: "Expired"
};
var RequestUrgency = {
  STANDARD: "STANDARD",
  EXPEDITED: "EXPEDITED"
};
var REQUEST_URGENCY_EXPIRY_DAYS = {
  STANDARD: 15,
  EXPEDITED: 7
};
var PORTFOLIO_LINK_TYPES = {
  PORTFOLIO: "PORTFOLIO",
  DEAL_ACCESS: "DEAL_ACCESS"
};
function assessmentStateToRequestProgress(workflowState) {
  switch (workflowState) {
    case "COLLECTING_EVIDENCE":
    case "COLLECTING_DOCUMENTS":
    // backward compat for cached/logged old values
    case "CODE_SCANNING":
      return "Evidence";
    case "ANSWERING_QUESTIONS":
    case "ANSWERS_SUBMITTED":
      return "Questions";
    case "REVIEWING_REPORT":
      return "Review";
    case "SHARED":
    case "FINALIZED":
      return "Completed";
    default:
      return "Unknown";
  }
}
var FindingType = {
  CONCERN: "concern",
  STRENGTH: "strength"
};
var ManagementNoteStatus = {
  DRAFT: "DRAFT",
  PUBLISHED: "PUBLISHED"
};
var DocumentStatus = {
  UPLOADING: "uploading",
  UPLOADED: "uploaded",
  TEXT_EXTRACTED: "text_extracted",
  EMBEDDINGS_GENERATED: "embeddings_generated",
  PROCESSING: "processing",
  COMPLETE: "complete",
  FAILED: "failed"
};
var DocumentStatusLabels = {
  [DocumentStatus.UPLOADING]: "Uploading",
  [DocumentStatus.UPLOADED]: "Uploaded",
  [DocumentStatus.TEXT_EXTRACTED]: "Text Extracted",
  [DocumentStatus.EMBEDDINGS_GENERATED]: "Embeddings Generated",
  [DocumentStatus.PROCESSING]: "Processing",
  [DocumentStatus.COMPLETE]: "Complete",
  [DocumentStatus.FAILED]: "Failed"
};
var CompanyStage = {
  PRE_SEED: "pre-seed",
  SEED: "seed",
  SERIES_A: "series-a",
  SERIES_B: "series-b",
  GROWTH: "growth",
  MATURE: "mature"
};
var CompanyStageLabels = {
  [CompanyStage.PRE_SEED]: "Pre-seed",
  [CompanyStage.SEED]: "Seed",
  [CompanyStage.SERIES_A]: "Series A",
  [CompanyStage.SERIES_B]: "Series B",
  [CompanyStage.GROWTH]: "Growth",
  [CompanyStage.MATURE]: "Mature"
};
var AssessmentPurpose = {
  FUNDRAISING: "fundraising",
  M_AND_A: "m-a",
  INTERNAL_AUDIT: "internal-audit",
  BOARD_UPDATE: "board-update"
};
var AssessmentPurposeLabels = {
  [AssessmentPurpose.FUNDRAISING]: "Fundraising",
  [AssessmentPurpose.M_AND_A]: "M&A / Acquisition",
  [AssessmentPurpose.INTERNAL_AUDIT]: "Internal Audit",
  [AssessmentPurpose.BOARD_UPDATE]: "Board Update"
};
var QuestionPriority = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low"
};
var QuestionPriorityLabels = {
  [QuestionPriority.HIGH]: "High",
  [QuestionPriority.MEDIUM]: "Medium",
  [QuestionPriority.LOW]: "Low"
};
var QuestionPriorityColors = {
  [QuestionPriority.HIGH]: "bg-red-100 text-red-800",
  [QuestionPriority.MEDIUM]: "bg-yellow-100 text-yellow-800",
  [QuestionPriority.LOW]: "bg-gray-100 text-gray-800"
};
var AnalysisDepth = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low"
};
var AnalysisDepthLabels = {
  [AnalysisDepth.HIGH]: "High",
  [AnalysisDepth.MEDIUM]: "Medium",
  [AnalysisDepth.LOW]: "Low"
};
var AnalysisDepthDescriptions = {
  [AnalysisDepth.HIGH]: "Reasonable investigation with high confidence. Materials provided and time allocated allowed for thorough coverage.",
  [AnalysisDepth.MEDIUM]: "Basic investigation with moderate confidence. Restricted by materials provided, time allocated, access to people, or slightly reduced scope.",
  [AnalysisDepth.LOW]: "Cursory coverage with low confidence. Few or no materials provided, very limited interview time, or area was not fully explored."
};
var AnalysisDepthColors = {
  [AnalysisDepth.HIGH]: "bg-green-100 text-green-800",
  [AnalysisDepth.MEDIUM]: "bg-yellow-100 text-yellow-800",
  [AnalysisDepth.LOW]: "bg-gray-100 text-gray-800"
};
var FindingSeverity = {
  CRITICAL: "CRITICAL",
  HIGH: "HIGH",
  MEDIUM: "MEDIUM",
  LOW: "LOW",
  INFO: "INFO"
};
var FindingSeverityLabels = {
  [FindingSeverity.CRITICAL]: "Critical",
  [FindingSeverity.HIGH]: "High",
  [FindingSeverity.MEDIUM]: "Medium",
  [FindingSeverity.LOW]: "Low",
  [FindingSeverity.INFO]: "Info"
};
var FindingSeverityColors = {
  [FindingSeverity.CRITICAL]: "text-red-700 bg-red-50 border-red-200",
  [FindingSeverity.HIGH]: "text-orange-700 bg-orange-50 border-orange-200",
  [FindingSeverity.MEDIUM]: "text-yellow-700 bg-yellow-50 border-yellow-200",
  [FindingSeverity.LOW]: "text-green-700 bg-green-50 border-green-200",
  [FindingSeverity.INFO]: "text-blue-700 bg-blue-50 border-blue-200"
};
var FindingCategory = {
  SECURITY: "security",
  TESTING: "testing",
  ARCHITECTURE: "architecture",
  CODE_QUALITY: "code-quality",
  SECRETS: "secrets",
  // STA-284: SCA (OSV-Scanner) findings — dependency CVEs
  DEPENDENCY_VULNERABILITY: "dependency-vulnerability"
};
var CodeScanStatus = {
  PENDING: "pending",
  PROVISIONING: "provisioning",
  DOWNLOADING: "downloading",
  ANALYZING: "analyzing",
  COMPLETED: "completed",
  FAILED: "failed",
  BUDGET_EXCEEDED: "budget-exceeded",
  // STA-284 §3b: SCA pass failed in full-source mode but LLM passes
  // completed — report renders a "SCA unavailable" banner, scan is not
  // marked FAILED. Manifest-only mode still uses FAILED (no fallback).
  COMPLETED_WITH_WARNINGS: "completed-with-warnings"
};
var ACTIVE_CODE_SCAN_STATUSES = [
  CodeScanStatus.PENDING,
  CodeScanStatus.PROVISIONING,
  CodeScanStatus.DOWNLOADING,
  CodeScanStatus.ANALYZING
];
var TERMINAL_CODE_SCAN_STATUSES = [
  CodeScanStatus.COMPLETED,
  CodeScanStatus.FAILED,
  CodeScanStatus.BUDGET_EXCEEDED,
  CodeScanStatus.COMPLETED_WITH_WARNINGS
];
var ACTIVE_CODE_SCAN_STATUS_SET = new Set(ACTIVE_CODE_SCAN_STATUSES);
var TERMINAL_CODE_SCAN_STATUS_SET = new Set(
  TERMINAL_CODE_SCAN_STATUSES
);
function isActiveCodeScanStatus(status) {
  return status !== null && status !== void 0 && ACTIVE_CODE_SCAN_STATUS_SET.has(status);
}
function isTerminalCodeScanStatus(status) {
  return status !== null && status !== void 0 && TERMINAL_CODE_SCAN_STATUS_SET.has(status);
}
var CodeScanMode = {
  FULL_SOURCE: "full-source",
  MANIFEST_ONLY: "manifest-only"
};
var CodeScanFindingSource = {
  SEMGREP: "semgrep",
  LLM: "llm",
  OSV_SCANNER: "osv-scanner"
};
function emptyFindingCounts() {
  return { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 };
}
function emptyFindingCountsByStatus() {
  return {
    CRITICAL: { failed: 0, passed: 0 },
    HIGH: { failed: 0, passed: 0 },
    MEDIUM: { failed: 0, passed: 0 },
    LOW: { failed: 0, passed: 0 },
    INFO: { failed: 0, passed: 0 }
  };
}
var DEPENDENCY_ECOSYSTEMS = [
  "npm",
  "PyPI",
  "Go",
  "RubyGems",
  "crates.io",
  "Packagist",
  "NuGet"
];
var QuestionCategory = {
  ARCHITECTURE: "Architecture",
  SDLC: "SDLC",
  DEVOPS_INFRASTRUCTURE: "DevOps & Infrastructure",
  PRODUCT_MANAGEMENT: "Product Management",
  TEAM_ORGANIZATION: "Team & Organization"
};
var ResponseStatus = {
  UNANSWERED: "unanswered",
  ANSWERED: "answered",
  DRAFT: "draft",
  REVIEW: "review",
  FINAL: "final"
};
var ResponseStatusLabels = {
  [ResponseStatus.UNANSWERED]: "Unanswered",
  [ResponseStatus.ANSWERED]: "Answered",
  [ResponseStatus.DRAFT]: "Draft",
  [ResponseStatus.REVIEW]: "Under Review",
  [ResponseStatus.FINAL]: "Final"
};
var ResponseStatusColors = {
  [ResponseStatus.UNANSWERED]: "bg-gray-100 text-gray-800",
  [ResponseStatus.ANSWERED]: "bg-green-100 text-green-800",
  [ResponseStatus.DRAFT]: "bg-blue-100 text-blue-800",
  [ResponseStatus.REVIEW]: "bg-yellow-100 text-yellow-800",
  [ResponseStatus.FINAL]: "bg-green-100 text-green-800"
};
var QuestionAssignmentStatus = {
  UNASSIGNED: "unassigned",
  ASSIGNED: "assigned"
};
var QuestionAssignmentStatusLabels = {
  [QuestionAssignmentStatus.UNASSIGNED]: "Unassigned",
  [QuestionAssignmentStatus.ASSIGNED]: "Assigned"
};
function getQuestionAssignmentStatus(assignedToUserId) {
  return assignedToUserId ? QuestionAssignmentStatus.ASSIGNED : QuestionAssignmentStatus.UNASSIGNED;
}
var QuestionCategories = Object.values(QuestionCategory);
var DIMENSION_SLUGS = {
  Architecture: "architecture",
  SDLC: "sdlc",
  "DevOps & Infrastructure": "devops-infrastructure",
  "Product Management": "product-management",
  "Team & Organization": "team-organization"
};
function toStringArray(val) {
  if (Array.isArray(val))
    return val.filter((v) => typeof v === "string");
  return [];
}
var QuestionCategoryShortLabels = {
  [QuestionCategory.ARCHITECTURE]: "Architecture",
  [QuestionCategory.SDLC]: "SDLC",
  [QuestionCategory.DEVOPS_INFRASTRUCTURE]: "DevOps",
  [QuestionCategory.PRODUCT_MANAGEMENT]: "Product",
  [QuestionCategory.TEAM_ORGANIZATION]: "Team"
};
var UserRole = {
  ADMIN: "admin",
  MEMBER: "member"
};
var UserRoleLabels = {
  [UserRole.ADMIN]: "Admin",
  [UserRole.MEMBER]: "Member"
};
var JobType = {
  TEXT_EXTRACTION: "TEXT_EXTRACTION",
  EMBEDDING_GENERATION: "EMBEDDING_GENERATION",
  LLM_ANALYSIS: "LLM_ANALYSIS",
  QUESTION_GENERATION: "QUESTION_GENERATION"
};
var PASSWORD_MIN_LENGTH = 8;
var PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&._\-#+,=])[A-Za-z\d@$!%*?&._\-#+,=]{8,}$/;
var PASSWORD_ERROR_MESSAGE = "Password must contain at least 8 characters, including uppercase, lowercase, number, and special character";
var MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
var MAX_FILE_SIZE_MB = 50;
var ACCEPTED_FILE_TYPES = {
  PDF: "application/pdf",
  DOCX: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  DOC: "application/msword",
  XLSX: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  XLS: "application/vnd.ms-excel",
  CSV: "text/csv",
  PPTX: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  PPT: "application/vnd.ms-powerpoint",
  TXT: "text/plain",
  MD: "text/markdown",
  PNG: "image/png",
  JPG: "image/jpeg",
  JPEG: "image/jpeg",
  SVG: "image/svg+xml"
};
var SUPPORTED_FILE_EXTENSIONS = [
  // Documents
  "pdf",
  "docx",
  "doc",
  "xlsx",
  "xls",
  "pptx",
  "ppt",
  "csv",
  "txt",
  "md",
  // Images
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  // Technical formats
  "html",
  "json",
  "yaml",
  "yml",
  "sql",
  "dbml",
  "vsdx",
  "drawio"
];
var DOCUMENT_EXTENSIONS = [
  "pdf",
  "docx",
  "doc",
  "xlsx",
  "xls",
  "csv",
  "pptx",
  "ppt",
  "txt",
  "md"
];
var IMAGE_EXTENSIONS = [
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg"
];
var VISION_COMPATIBLE_IMAGE_EXTENSIONS = [
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp"
];
var DOCUMENT_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  // DOCX
  "application/msword",
  // DOC
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  // XLSX
  "application/vnd.ms-excel",
  // XLS
  "text/csv",
  // CSV
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // PPTX
  "application/vnd.ms-powerpoint",
  // PPT
  "text/plain",
  // TXT
  "text/markdown"
  // MD
];
var IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif"
];
var MAX_VISION_IMAGE_DIMENSION = 8e3;
var RiskImpact = {
  CRITICAL: "CRITICAL",
  HIGH: "HIGH",
  MEDIUM: "MEDIUM",
  LOW: "LOW"
};
var RiskImpactLabels = {
  [RiskImpact.CRITICAL]: "Critical",
  [RiskImpact.HIGH]: "High",
  [RiskImpact.MEDIUM]: "Medium",
  [RiskImpact.LOW]: "Low"
};
var RiskLikelihood4 = {
  HIGH: "HIGH",
  MEDIUM: "MEDIUM",
  LOW: "LOW",
  MINIMAL: "MINIMAL"
};
var RiskLikelihood4Labels = {
  [RiskLikelihood4.HIGH]: "High",
  [RiskLikelihood4.MEDIUM]: "Medium",
  [RiskLikelihood4.LOW]: "Low",
  [RiskLikelihood4.MINIMAL]: "Minimal"
};
var LIKELIHOOD_COLLAPSE_MAP = {
  very_likely: "HIGH",
  likely: "HIGH",
  moderate: "MEDIUM",
  unlikely: "LOW",
  very_unlikely: "MINIMAL"
};
var RISK_MATRIX_ZONE_STYLES = {
  danger: {
    bg: "bg-red-100",
    border: "border-red-300",
    text: "text-red-900",
    label: "Danger"
  },
  high: {
    bg: "bg-orange-100",
    border: "border-orange-300",
    text: "text-orange-900",
    label: "High Risk"
  },
  moderate: {
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    text: "text-yellow-900",
    label: "Moderate"
  },
  low: {
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-900",
    label: "Low Risk"
  }
};
var RISK_MATRIX_ZONE_HEX = {
  danger: { bg: "#fee2e2", border: "#fca5a5", text: "#7f1d1d" },
  high: { bg: "#ffedd5", border: "#fdba74", text: "#7c2d12" },
  moderate: { bg: "#fefce8", border: "#fde047", text: "#713f12" },
  low: { bg: "#f0fdf4", border: "#86efac", text: "#14532d" }
};
var ACCESS_TOKEN_TTL_MINUTES = 15;
var REFRESH_TOKEN_TTL_DAYS = 7;
var EMAIL_VERIFICATION_TOKEN_TTL_HOURS = 24;
var PRESIGNED_URL_TTL_MINUTES = 10;
var LOGIN_RATE_LIMIT_ATTEMPTS = 5;
var LOGIN_RATE_LIMIT_WINDOW_MINUTES = 15;
var DEFAULT_PAGE_SIZE = 20;
var MAX_PAGE_SIZE = 100;
var EMBEDDING_CHUNK_SIZE_TOKENS = 512;
var EMBEDDING_CHUNK_OVERLAP_TOKENS = 50;
var LLM_MODEL_CLAUDE = "claude-opus-4-6";
var LLM_MODEL_OPENAI_EMBEDDINGS = "text-embedding-3-small";
var LLM_MAX_TOKENS_ANALYSIS = 8e3;
var LLM_MAX_TOKENS_QUESTIONS = 6e3;
var LLM_TEMPERATURE_ANALYSIS = 0.3;
var LLM_TEMPERATURE_QUESTIONS = 0.5;
var MIN_QUESTIONS_PER_ASSESSMENT = 30;
var MAX_QUESTIONS_PER_ASSESSMENT = 50;
var QUESTION_PRIORITY_DISTRIBUTION = {
  [QuestionPriority.HIGH]: 0.2,
  // 20%
  [QuestionPriority.MEDIUM]: 0.5,
  // 50%
  [QuestionPriority.LOW]: 0.3
  // 30%
};
var ReportType = {
  INVESTMENT_MEMORANDUM: "INVESTMENT_MEMORANDUM",
  TECHNICAL_ASSESSMENT: "TECHNICAL_ASSESSMENT"
};
var EvidenceSource = {
  UPLOAD: "UPLOAD",
  AGENT: "AGENT"
};
var EvidenceSourceLabels = {
  UPLOAD: "Uploaded by you",
  AGENT: "Submitted by agent"
};
var MAX_TEXT_SUBMISSION_LENGTH = 5e4;
var MAX_SOURCE_LABEL_LENGTH = 120;
var AGENT_SUBMISSION_USER_ID = "agent";
var Stage = {
  PRE_SEED: "pre-seed",
  SEED: "seed",
  SERIES_A: "series-a",
  SERIES_B_PLUS: "series-b-plus",
  M_AND_A: "m-and-a"
};
var STAGE_ORDER = [
  Stage.PRE_SEED,
  Stage.SEED,
  Stage.SERIES_A,
  Stage.SERIES_B_PLUS,
  Stage.M_AND_A
];
var StageLabels = {
  [Stage.PRE_SEED]: "Pre-seed",
  [Stage.SEED]: "Seed",
  [Stage.SERIES_A]: "Series A",
  [Stage.SERIES_B_PLUS]: "Series B+",
  [Stage.M_AND_A]: "M&A"
};
function stageAtLeast(stage, minStage) {
  const stageIdx = STAGE_ORDER.indexOf(stage);
  const minIdx = STAGE_ORDER.indexOf(minStage);
  if (stageIdx === -1 || minIdx === -1) {
    throw new Error(
      `stageAtLeast: unknown stage (stage=${stage}, minStage=${minStage})`
    );
  }
  return stageIdx >= minIdx;
}
var DealType = {
  PIPELINE: "pipeline",
  PORTFOLIO: "portfolio"
};
var FocusLens = {
  /** Defensibility & AI maturity — always on (2026 IC table-stakes, §2.3). */
  DEFENSIBILITY_AI: "defensibility-ai",
  /** Architecture & scalability. */
  ARCHITECTURE_SCALABILITY: "architecture-scalability",
  /** Security & compliance posture. */
  SECURITY_COMPLIANCE: "security-compliance",
  /** Engineering & delivery maturity. */
  ENGINEERING_DELIVERY: "engineering-delivery",
  /** Infrastructure & reliability. */
  INFRASTRUCTURE_RELIABILITY: "infrastructure-reliability"
};
var FocusLensLabels = {
  [FocusLens.DEFENSIBILITY_AI]: "Defensibility & AI maturity",
  [FocusLens.ARCHITECTURE_SCALABILITY]: "Architecture & scalability",
  [FocusLens.SECURITY_COMPLIANCE]: "Security & compliance posture",
  [FocusLens.ENGINEERING_DELIVERY]: "Engineering & delivery maturity",
  [FocusLens.INFRASTRUCTURE_RELIABILITY]: "Infrastructure & reliability"
};
var DepthTier = {
  DEEP_DIVE: "deep-dive",
  STANDARD: "standard"
};
var AnalysisRead = {
  ARCHITECTURE: "Architecture",
  SDLC: "SDLC",
  DEVOPS_INFRASTRUCTURE: "DevOps & Infrastructure",
  PRODUCT_MANAGEMENT: "Product Management",
  TEAM_ORGANIZATION: "Team & Organization",
  DEFENSIBILITY: "Defensibility",
  AI_MATURITY: "AI Maturity"
};
var ANALYSIS_READS = [
  AnalysisRead.DEFENSIBILITY,
  AnalysisRead.AI_MATURITY,
  AnalysisRead.ARCHITECTURE,
  AnalysisRead.SDLC,
  AnalysisRead.DEVOPS_INFRASTRUCTURE,
  AnalysisRead.PRODUCT_MANAGEMENT,
  AnalysisRead.TEAM_ORGANIZATION
];
var ALWAYS_ON_READS = [
  AnalysisRead.DEFENSIBILITY,
  AnalysisRead.AI_MATURITY
];
var QUESTION_DIMENSION_READS = [
  AnalysisRead.ARCHITECTURE,
  AnalysisRead.SDLC,
  AnalysisRead.DEVOPS_INFRASTRUCTURE,
  AnalysisRead.PRODUCT_MANAGEMENT,
  AnalysisRead.TEAM_ORGANIZATION
];
var FillMethod = {
  UPLOAD: "upload",
  TYPED: "typed",
  MCP: "mcp",
  CONNECT: "connect"
};
var EvidenceSlotState = {
  EMPTY: "empty",
  PROVIDED: "provided",
  NA: "na"
};
var Requiredness = {
  DEPTH_DRIVEN: "depth-driven",
  COVERAGE_ANCHOR: "coverage-anchor",
  OPTIONAL: "optional"
};

// src/risk-matrix.ts
var IMPACT_LEVELS = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
var LIKELIHOOD_LEVELS = ["HIGH", "MEDIUM", "LOW", "MINIMAL"];
var ZONE_MAP = [
  // CRITICAL impact
  ["danger", "danger", "high", "high"],
  // HIGH impact
  ["danger", "high", "high", "moderate"],
  // MEDIUM impact
  ["high", "moderate", "moderate", "low"],
  // LOW impact
  ["moderate", "low", "low", "low"]
];
function mapLikelihoodToFour(likelihood) {
  if (!likelihood) return "MEDIUM";
  return LIKELIHOOD_COLLAPSE_MAP[likelihood.toLowerCase()] ?? "MEDIUM";
}
function getCellZone(impact, likelihood) {
  const impactIdx = IMPACT_LEVELS.indexOf(impact);
  const likelihoodIdx = LIKELIHOOD_LEVELS.indexOf(likelihood);
  return ZONE_MAP[impactIdx]?.[likelihoodIdx] ?? "moderate";
}
function buildRiskMatrix(analyses) {
  const cells = Array.from(
    { length: 4 },
    () => Array.from({ length: 4 }, () => [])
  );
  let totalConcerns = 0;
  let skippedConcerns = 0;
  for (const analysis of analyses) {
    const raw = analysis.rawLlmResponse;
    const concerns = raw?.concerns ?? [];
    for (const concern of concerns) {
      if (!concern.risk_impact) {
        skippedConcerns++;
        continue;
      }
      const impact = concern.risk_impact.toUpperCase();
      if (!IMPACT_LEVELS.includes(impact)) {
        skippedConcerns++;
        continue;
      }
      const likelihood4 = mapLikelihoodToFour(concern.risk_likelihood);
      const impactIdx = IMPACT_LEVELS.indexOf(impact);
      const likelihoodIdx = LIKELIHOOD_LEVELS.indexOf(likelihood4);
      cells[impactIdx][likelihoodIdx].push({
        finding: concern.finding,
        dimension: analysis.dimensionName,
        evidence: concern.evidence ?? "",
        likelihood4,
        impact
      });
      totalConcerns++;
    }
  }
  return { cells, totalConcerns, skippedConcerns };
}

// src/investor.ts
var RISK_SEVERITY = {
  CRITICAL: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1
};
function isActiveDeal(request) {
  return !request.portfolioLinked && request.investedAt === null && request.archivedAt === null && (request.status === RequestStatus.INVITED || request.status === RequestStatus.IN_PROGRESS);
}
function countActiveDeals(requests) {
  return requests.reduce((n, r) => isActiveDeal(r) ? n + 1 : n, 0);
}

// src/portfolio-dashboard.ts
var STALE_ASSESSMENT_MONTHS = 12;
var INVITATION_EXPIRY_WINDOW_DAYS = 3;
var HIGH_OR_ABOVE = RISK_SEVERITY.HIGH;
var MIN_PATTERN_AFFECTED = 2;
function toMillis(value) {
  return value instanceof Date ? value.getTime() : new Date(value).getTime();
}
function hasSharedAssessment(entry) {
  return entry.status === "active";
}
function buildRiskDistribution(entries) {
  const dist = {
    CRITICAL: 0,
    HIGH: 0,
    MEDIUM: 0,
    LOW: 0,
    unassessed: 0
  };
  for (const entry of entries) {
    if (entry.status !== "active" || entry.overallRiskLevel === null) {
      dist.unassessed += 1;
      continue;
    }
    dist[entry.overallRiskLevel] += 1;
  }
  return dist;
}
function worstDimensionsPerEntry(entry) {
  const worstByDimension = /* @__PURE__ */ new Map();
  for (const dim of entry.dimensions) {
    const existing = worstByDimension.get(dim.dimensionName);
    if (!existing || RISK_SEVERITY[dim.riskLevel] > RISK_SEVERITY[existing.riskLevel]) {
      worstByDimension.set(dim.dimensionName, dim);
    }
  }
  return [...worstByDimension.values()];
}
function tallyDimensions(shared) {
  const tallies = /* @__PURE__ */ new Map();
  for (const entry of shared) {
    for (const dim of worstDimensionsPerEntry(entry)) {
      let tally = tallies.get(dim.dimensionName);
      if (!tally) {
        tally = {
          dimensionName: dim.dimensionName,
          highPlusCount: 0,
          highPlusSeverity: 0,
          lowCount: 0
        };
        tallies.set(dim.dimensionName, tally);
      }
      const severity = RISK_SEVERITY[dim.riskLevel];
      if (severity >= HIGH_OR_ABOVE) {
        tally.highPlusCount += 1;
        tally.highPlusSeverity += severity;
      }
      if (dim.riskLevel === "LOW") {
        tally.lowCount += 1;
      }
    }
  }
  return tallies;
}
function buildPortfolioPatterns(entries) {
  const shared = entries.filter(hasSharedAssessment);
  const totalShared = shared.length;
  if (totalShared < 3) return [];
  const tallies = [...tallyDimensions(shared).values()];
  const patterns = [];
  const flaggable = tallies.filter(
    (t) => t.highPlusCount >= MIN_PATTERN_AFFECTED
  );
  if (flaggable.length > 0) {
    const winner = [...flaggable].sort((a, b) => {
      if (b.highPlusCount !== a.highPlusCount)
        return b.highPlusCount - a.highPlusCount;
      if (b.highPlusSeverity !== a.highPlusSeverity)
        return b.highPlusSeverity - a.highPlusSeverity;
      return a.dimensionName.localeCompare(b.dimensionName);
    })[0];
    patterns.push({
      kind: "most-flagged-dimension",
      dimensionName: winner.dimensionName,
      affectedCount: winner.highPlusCount,
      totalShared,
      text: `${winner.highPlusCount} of ${totalShared} companies flag ${winner.dimensionName} at HIGH or above`
    });
  }
  const lowCounts = tallies.map((t) => t.lowCount);
  const allLowEqual = new Set(lowCounts).size <= 1;
  const strongest = [...tallies].filter((t) => t.lowCount >= MIN_PATTERN_AFFECTED).sort((a, b) => {
    if (b.lowCount !== a.lowCount) return b.lowCount - a.lowCount;
    return a.dimensionName.localeCompare(b.dimensionName);
  })[0];
  if (!allLowEqual && strongest) {
    patterns.push({
      kind: "strongest-dimension",
      dimensionName: strongest.dimensionName,
      affectedCount: strongest.lowCount,
      totalShared,
      text: `${strongest.lowCount} of ${totalShared} companies rate ${strongest.dimensionName} LOW`
    });
  }
  return patterns;
}
var KIND_SEVERITY = {
  "critical-findings": "CRITICAL",
  "invitation-expiring": "HIGH",
  "pending-share": "MEDIUM",
  "stale-assessment": "MEDIUM"
};
function isStaleAssessment(latest, now, thresholdMonths = STALE_ASSESSMENT_MONTHS) {
  if (latest === null) return false;
  const latestMs = toMillis(latest);
  if (Number.isNaN(latestMs)) return false;
  const cutoff = new Date(toMillis(now));
  cutoff.setMonth(cutoff.getMonth() - thresholdMonths);
  return latestMs < cutoff.getTime();
}
function buildActionItems(entries, invitations, now) {
  const nowMs = toMillis(now);
  const expiryWindowMs = INVITATION_EXPIRY_WINDOW_DAYS * 24 * 60 * 60 * 1e3;
  const items = [];
  for (const entry of entries) {
    if (entry.findingCounts.CRITICAL > 0) {
      items.push({
        kind: "critical-findings",
        severity: KIND_SEVERITY["critical-findings"],
        companyName: entry.companyName,
        companyTenantId: entry.companyTenantId,
        occurredAt: entry.sharedAt ?? entry.finalizedAt ?? new Date(nowMs).toISOString(),
        meta: { criticalCount: entry.findingCounts.CRITICAL }
      });
    }
    if (entry.status === "pending_share") {
      items.push({
        kind: "pending-share",
        severity: KIND_SEVERITY["pending-share"],
        companyName: entry.companyName,
        companyTenantId: entry.companyTenantId,
        occurredAt: entry.finalizedAt ?? new Date(nowMs).toISOString(),
        meta: {}
      });
    }
    const latestDate = entry.sharedAt ?? entry.finalizedAt;
    if (entry.status === "active" && latestDate !== null && isStaleAssessment(latestDate, now)) {
      items.push({
        kind: "stale-assessment",
        severity: KIND_SEVERITY["stale-assessment"],
        companyName: entry.companyName,
        companyTenantId: entry.companyTenantId,
        occurredAt: latestDate,
        meta: { assessedAt: latestDate }
      });
    }
  }
  for (const invitation of invitations) {
    if (invitation.status !== "pending") continue;
    const expiresMs = toMillis(invitation.expiresAt);
    if (expiresMs >= nowMs && expiresMs <= nowMs + expiryWindowMs) {
      items.push({
        kind: "invitation-expiring",
        severity: KIND_SEVERITY["invitation-expiring"],
        occurredAt: invitation.expiresAt,
        meta: {
          companyEmail: invitation.companyEmail,
          expiresAt: invitation.expiresAt
        }
      });
    }
  }
  items.sort((a, b) => {
    const severityDelta = RISK_SEVERITY[b.severity] - RISK_SEVERITY[a.severity];
    if (severityDelta !== 0) return severityDelta;
    return toMillis(b.occurredAt) - toMillis(a.occurredAt);
  });
  return items;
}

// src/assessment-milestone.ts
var AssessmentMilestone = {
  /**
   * The company has been invited and has not started evidence collection —
   * whether or not it has accepted yet (an unanswered request also maps here).
   */
  INVITED: "INVITED",
  /** The company started the assessment (collecting evidence). */
  STARTED: "STARTED",
  /** The company is answering the generated questions. */
  ANSWERING: "ANSWERING",
  /** Answers submitted; StackVerify is analysing and drafting the report. */
  ANALYSIS: "ANALYSIS",
  /** The company is reviewing the drafted report before sharing. */
  COMPANY_REVIEW: "COMPANY_REVIEW",
  /** The assessment has been shared with the investor. */
  SHARED: "SHARED"
};
var MILESTONE_SEQUENCE = [
  AssessmentMilestone.INVITED,
  AssessmentMilestone.STARTED,
  AssessmentMilestone.ANSWERING,
  AssessmentMilestone.ANALYSIS,
  AssessmentMilestone.COMPANY_REVIEW,
  AssessmentMilestone.SHARED
];
var AssessmentMilestoneLabels = {
  // Not "Invitation accepted": INVITED also covers unanswered requests (sv-m5hz).
  [AssessmentMilestone.INVITED]: "Invited",
  [AssessmentMilestone.STARTED]: "Evidence collection",
  [AssessmentMilestone.ANSWERING]: "Answering questions",
  [AssessmentMilestone.ANALYSIS]: "Analysis & report",
  [AssessmentMilestone.COMPANY_REVIEW]: "Company review",
  [AssessmentMilestone.SHARED]: "Shared with you"
};
var TERMINAL_REQUEST_STATUSES = /* @__PURE__ */ new Set([
  RequestStatus.DECLINED,
  RequestStatus.CANCELED,
  RequestStatus.EXPIRED
]);
function milestoneIndex(milestone) {
  return MILESTONE_SEQUENCE.indexOf(milestone);
}
function isTerminalRequest(status) {
  return TERMINAL_REQUEST_STATUSES.has(status);
}
var WORKFLOW_STATE_TO_MILESTONE = {
  [AssessmentWorkflowState.COLLECTING_EVIDENCE]: AssessmentMilestone.STARTED,
  [AssessmentWorkflowState.ANSWERING_QUESTIONS]: AssessmentMilestone.ANSWERING,
  [AssessmentWorkflowState.ANSWERS_SUBMITTED]: AssessmentMilestone.ANALYSIS,
  [AssessmentWorkflowState.REVIEWING_REPORT]: AssessmentMilestone.COMPANY_REVIEW,
  [AssessmentWorkflowState.SHARED]: AssessmentMilestone.SHARED,
  [AssessmentWorkflowState.FINALIZED]: AssessmentMilestone.SHARED
};
function deriveMilestone(args) {
  const { status, hasLinkedAssessment, workflowState, archived, floor } = args;
  if (archived || isTerminalRequest(status)) return null;
  const derived = deriveRawMilestone(
    status,
    hasLinkedAssessment,
    workflowState
  );
  if (floor && milestoneIndex(floor) > milestoneIndex(derived)) {
    return floor;
  }
  return derived;
}
function deriveRawMilestone(status, hasLinkedAssessment, workflowState) {
  if (status === RequestStatus.COMPLETED || hasLinkedAssessment) {
    return AssessmentMilestone.SHARED;
  }
  if (workflowState !== null) {
    return WORKFLOW_STATE_TO_MILESTONE[workflowState];
  }
  return AssessmentMilestone.INVITED;
}

// src/evidence-tracks.ts
var INFRA_SCANNING_ENABLED = true;
var EvidenceTrackId = {
  DOCUMENTS: "documents",
  CODE_SCAN: "codeScan",
  INFRASTRUCTURE: "infrastructure"
};
var EvidenceTrackStatus = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETE: "COMPLETE",
  SKIPPED: "SKIPPED",
  COMING_SOON: "COMING_SOON"
};
var EvidenceTrackLabels = {
  [EvidenceTrackId.DOCUMENTS]: "Documents",
  [EvidenceTrackId.CODE_SCAN]: "Code Scan",
  [EvidenceTrackId.INFRASTRUCTURE]: "Infrastructure"
};
var DocumentsTrackStatus = {
  OPEN: "OPEN",
  COMPLETE: "COMPLETE",
  SKIPPED: "SKIPPED"
};
var TERMINAL_INFRA_STATUSES = /* @__PURE__ */ new Set(["completed", "failed", "cancelled"]);
function deriveDocumentsStatus(documentsStatus, documentCount) {
  if (documentsStatus === DocumentsTrackStatus.COMPLETE) {
    return {
      status: EvidenceTrackStatus.COMPLETE,
      detail: `${documentCount} uploaded`
    };
  }
  if (documentsStatus === DocumentsTrackStatus.SKIPPED) {
    return { status: EvidenceTrackStatus.SKIPPED };
  }
  if (documentCount > 0) {
    return {
      status: EvidenceTrackStatus.IN_PROGRESS,
      detail: `${documentCount} uploaded`
    };
  }
  return { status: EvidenceTrackStatus.NOT_STARTED };
}
function deriveCodeScanStatus(codeScanSkipped, codeScanStatuses) {
  if (codeScanSkipped) {
    return { status: EvidenceTrackStatus.SKIPPED };
  }
  if (codeScanStatuses.length === 0) {
    return { status: EvidenceTrackStatus.NOT_STARTED };
  }
  const activeCount = codeScanStatuses.filter(
    (s) => !isTerminalCodeScanStatus(s)
  ).length;
  if (activeCount > 0) {
    return {
      status: EvidenceTrackStatus.IN_PROGRESS,
      detail: `Scanning ${activeCount} repo${activeCount > 1 ? "s" : ""}`
    };
  }
  const total = codeScanStatuses.length;
  return {
    status: EvidenceTrackStatus.COMPLETE,
    detail: `${total} repo${total > 1 ? "s" : ""} scanned`
  };
}
function deriveInfraStatus(infraSkipped, infraScanStatuses) {
  if (!INFRA_SCANNING_ENABLED) {
    return {
      status: EvidenceTrackStatus.COMING_SOON,
      detail: "Coming soon"
    };
  }
  const hasActive = infraScanStatuses.some(
    (s) => !TERMINAL_INFRA_STATUSES.has(s)
  );
  if (hasActive) {
    return {
      status: EvidenceTrackStatus.IN_PROGRESS,
      detail: "Scanning infrastructure..."
    };
  }
  if (infraScanStatuses.includes("completed")) {
    return {
      status: EvidenceTrackStatus.COMPLETE,
      detail: "Scan complete"
    };
  }
  if (infraSkipped) {
    return { status: EvidenceTrackStatus.SKIPPED };
  }
  if (infraScanStatuses.length === 0) {
    return { status: EvidenceTrackStatus.NOT_STARTED };
  }
  const detail = infraScanStatuses.every((s) => s === "cancelled") ? "Scan cancelled" : "Scan failed";
  return {
    status: EvidenceTrackStatus.NOT_STARTED,
    detail
  };
}
function deriveTrackStatuses(params) {
  const {
    documentsStatus,
    documentCount,
    codeScanSkipped,
    codeScanStatuses,
    infraSkipped,
    infraScanStatuses = []
  } = params;
  const docs = deriveDocumentsStatus(documentsStatus, documentCount);
  const code = deriveCodeScanStatus(codeScanSkipped, codeScanStatuses);
  const infra = deriveInfraStatus(infraSkipped, infraScanStatuses);
  const tracks = [
    { status: docs.status, hasData: documentCount > 0 },
    { status: code.status, hasData: codeScanStatuses.length > 0 },
    { status: infra.status, hasData: infraScanStatuses.length > 0 }
  ];
  const resolved = (s) => s === EvidenceTrackStatus.COMPLETE || s === EvidenceTrackStatus.SKIPPED || s === EvidenceTrackStatus.COMING_SOON;
  const activeTracks = tracks.filter(
    (t) => t.status !== EvidenceTrackStatus.COMING_SOON
  );
  const resolvedCount = activeTracks.filter((t) => resolved(t.status)).length;
  const totalCount = activeTracks.length;
  const allResolved = tracks.every((t) => resolved(t.status));
  const atLeastOneHasData = tracks.some((t) => t.hasData);
  const canStartAnalysis = allResolved && atLeastOneHasData;
  return {
    documents: {
      id: EvidenceTrackId.DOCUMENTS,
      label: EvidenceTrackLabels[EvidenceTrackId.DOCUMENTS],
      status: docs.status,
      detail: docs.detail
    },
    codeScan: {
      id: EvidenceTrackId.CODE_SCAN,
      label: EvidenceTrackLabels[EvidenceTrackId.CODE_SCAN],
      status: code.status,
      detail: code.detail
    },
    infrastructure: {
      id: EvidenceTrackId.INFRASTRUCTURE,
      label: EvidenceTrackLabels[EvidenceTrackId.INFRASTRUCTURE],
      status: infra.status,
      detail: infra.detail
    },
    canStartAnalysis,
    resolvedCount,
    totalCount
  };
}

// src/depth-engine.ts
var FOCUS_LENS_TO_READS = {
  [FocusLens.DEFENSIBILITY_AI]: [
    AnalysisRead.DEFENSIBILITY,
    AnalysisRead.AI_MATURITY
  ],
  [FocusLens.ARCHITECTURE_SCALABILITY]: [AnalysisRead.ARCHITECTURE],
  [FocusLens.SECURITY_COMPLIANCE]: [
    AnalysisRead.SDLC,
    AnalysisRead.ARCHITECTURE
  ],
  [FocusLens.ENGINEERING_DELIVERY]: [AnalysisRead.SDLC],
  [FocusLens.INFRASTRUCTURE_RELIABILITY]: [AnalysisRead.DEVOPS_INFRASTRUCTURE]
};
var READ_DEEP_DEPENDENCIES = {
  [AnalysisRead.DEFENSIBILITY]: [AnalysisRead.ARCHITECTURE]
};
function deriveDepth(_stage, focus) {
  const depth = standardDepthMap();
  for (const read of ALWAYS_ON_READS) depth[read] = DepthTier.DEEP_DIVE;
  applyFocusElevations(depth, focus);
  resolveDeepDependencies(depth);
  return depth;
}
function standardDepthMap() {
  const depth = {};
  for (const read of ANALYSIS_READS) {
    depth[read] = DepthTier.STANDARD;
  }
  return depth;
}
function applyFocusElevations(depth, focus) {
  for (const lens of focus) {
    for (const read of FOCUS_LENS_TO_READS[lens] ?? []) {
      depth[read] = DepthTier.DEEP_DIVE;
    }
  }
}
function resolveDeepDependencies(depth) {
  let changed = true;
  while (changed) {
    changed = false;
    for (const read of ANALYSIS_READS) {
      if (depth[read] !== DepthTier.DEEP_DIVE) continue;
      for (const dep of READ_DEEP_DEPENDENCIES[read] ?? []) {
        if (depth[dep] !== DepthTier.DEEP_DIVE) {
          depth[dep] = DepthTier.DEEP_DIVE;
          changed = true;
        }
      }
    }
  }
}
function effectiveMinStage(entry, regulated) {
  if (!regulated || entry.regulatedMinStage === void 0) {
    return entry.minStage;
  }
  return stageAtLeast(entry.minStage, entry.regulatedMinStage) ? entry.regulatedMinStage : entry.minStage;
}
function isSlotRequired(entry, derivedDepth, stage, regulated) {
  if (entry.requiredness === Requiredness.OPTIONAL) {
    return false;
  }
  const stageOk = stageAtLeast(stage, effectiveMinStage(entry, regulated));
  if (!stageOk) return false;
  switch (entry.requiredness) {
    case Requiredness.COVERAGE_ANCHOR:
      return true;
    case Requiredness.DEPTH_DRIVEN:
      return derivedDepth[entry.dimension] === DepthTier.DEEP_DIVE;
    default:
      return assertUnreachableRequiredness(entry.requiredness);
  }
}
function assertUnreachableRequiredness(policy) {
  throw new Error(`Unhandled Requiredness policy: ${JSON.stringify(policy)}`);
}
function buildEvidenceRequest(derivedDepth, stage, catalog, options = {}) {
  const { contractId = "", regulated = false } = options;
  return catalog.map((entry) => {
    let required;
    try {
      required = isSlotRequired(entry, derivedDepth, stage, regulated);
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      throw new Error(
        `buildEvidenceRequest: catalog slot "${entry.id}" (dimension=${entry.dimension}) has invalid reference data: ${detail}`,
        { cause: err }
      );
    }
    const slot = {
      id: entry.id,
      contractId,
      dimension: entry.dimension,
      label: entry.label,
      required,
      requiredness: entry.requiredness,
      fillMethods: [...entry.fillMethods],
      state: EvidenceSlotState.EMPTY
    };
    if (entry.description !== void 0) slot.description = entry.description;
    return slot;
  });
}
var DEEP_DIVE_QUESTION_BUDGET = {
  minQuestions: 6,
  maxQuestions: 10,
  probing: true
};
var STANDARD_QUESTION_BUDGET = {
  minQuestions: 0,
  maxQuestions: 2,
  probing: false
};
var QUESTION_DEPTH_BUDGETS = {
  [DepthTier.DEEP_DIVE]: DEEP_DIVE_QUESTION_BUDGET,
  [DepthTier.STANDARD]: STANDARD_QUESTION_BUDGET
};
function deriveQuestionPlan(derivedDepth) {
  const plans = QUESTION_DIMENSION_READS.map(
    (dimension) => {
      const rawTier = derivedDepth?.[dimension];
      const depth = rawTier === DepthTier.DEEP_DIVE ? DepthTier.DEEP_DIVE : DepthTier.STANDARD;
      const budget = QUESTION_DEPTH_BUDGETS[depth];
      return { dimension, depth, ...budget };
    }
  );
  const totalMin = plans.reduce((sum, p) => sum + p.minQuestions, 0);
  const totalMax = plans.reduce((sum, p) => sum + p.maxQuestions, 0);
  return { plans, totalMin, totalMax };
}

// src/evidence-board.ts
import { z } from "zod";
var DIMENSION_FOCUS_COPY = {
  [AnalysisRead.DEFENSIBILITY]: "how defensible and hard-to-replicate the product is",
  [AnalysisRead.AI_MATURITY]: "where AI/ML shows up in the product and how it's built on",
  [AnalysisRead.ARCHITECTURE]: "how the system is designed and how it holds up as it grows",
  [AnalysisRead.SDLC]: "how you build, review, and ship changes",
  [AnalysisRead.DEVOPS_INFRASTRUCTURE]: "where it runs and how it stays up and recoverable",
  [AnalysisRead.PRODUCT_MANAGEMENT]: "how product direction is set and prioritised",
  [AnalysisRead.TEAM_ORGANIZATION]: "how the team is structured and organised"
};
var DEFAULT_INVESTOR_NAME = "An investor";
function assembleBoardDimensions(slots, derivedDepth) {
  return ANALYSIS_READS.map((dimension) => {
    const forDimension = slots.filter((slot) => slot.dimension === dimension);
    const requiredSlots = forDimension.filter((slot) => slot.required);
    const optionalSlots = forDimension.filter((slot) => !slot.required);
    const depth = derivedDepth[dimension] ?? DepthTier.STANDARD;
    return {
      dimension,
      depth,
      treatment: depth === DepthTier.DEEP_DIVE ? "deep-dive" : "standard",
      focusCopy: DIMENSION_FOCUS_COPY[dimension],
      requiredSlots,
      optionalSlots,
      suggestedCount: optionalSlots.length
    };
  });
}
function computeBoardProgress(slots) {
  const required = slots.filter((slot) => slot.required);
  const requiredProvided = required.filter(
    (slot) => slot.state === EvidenceSlotState.PROVIDED
  ).length;
  const requiredResolved = required.filter(
    (slot) => slot.state === EvidenceSlotState.PROVIDED || slot.state === EvidenceSlotState.NA
  ).length;
  return {
    requiredProvided,
    requiredTotal: required.length,
    requiredResolved
  };
}
var EvidenceSlotSchema = z.object({
  id: z.string(),
  contractId: z.string(),
  dimension: z.nativeEnum(AnalysisRead),
  label: z.string(),
  description: z.string().optional(),
  required: z.boolean(),
  requiredness: z.nativeEnum(Requiredness).optional(),
  fillMethods: z.array(z.nativeEnum(FillMethod)),
  state: z.nativeEnum(EvidenceSlotState),
  filledVia: z.nativeEnum(FillMethod).optional(),
  value: z.string().optional(),
  naReason: z.string().optional(),
  assignee: z.string().optional()
});
var EvidenceBoardProgressSchema = z.object({
  requiredProvided: z.number(),
  requiredTotal: z.number(),
  requiredResolved: z.number()
});
var EvidenceBoardDimensionGroupSchema = z.object({
  dimension: z.nativeEnum(AnalysisRead),
  depth: z.nativeEnum(DepthTier),
  treatment: z.enum(["deep-dive", "standard"]),
  focusCopy: z.string(),
  requiredSlots: z.array(EvidenceSlotSchema),
  optionalSlots: z.array(EvidenceSlotSchema),
  suggestedCount: z.number()
});
var EvidenceBoardContractSummarySchema = z.object({
  id: z.string(),
  companyName: z.string(),
  stage: z.nativeEnum(Stage),
  focus: z.array(z.nativeEnum(FocusLens)),
  investorName: z.string(),
  version: z.number()
});
var EvidenceBoardSchema = z.object({
  contract: EvidenceBoardContractSummarySchema,
  dimensions: z.array(EvidenceBoardDimensionGroupSchema),
  progress: EvidenceBoardProgressSchema,
  suggestedCodeScan: z.boolean(),
  suggestedInfraScan: z.boolean(),
  suggestedByInvestorName: z.string().nullable()
});
var EvidenceRequestPreviewSchema = z.object({
  stage: z.nativeEnum(Stage),
  focus: z.array(z.nativeEnum(FocusLens)),
  // Every read is REQUIRED: a missing read must fail the parse, not silently
  // render as "Standard" on the Confirm step.
  derivedDepth: z.object(
    Object.fromEntries(
      ANALYSIS_READS.map((read) => [read, z.nativeEnum(DepthTier)])
    )
  ),
  requiredCount: z.number().int().nonnegative(),
  requiredSlots: z.array(
    z.object({
      id: z.string(),
      dimension: z.nativeEnum(AnalysisRead),
      label: z.string()
    })
  )
});
var EvidenceBoardSubmitResultSchema = z.object({
  contractVersion: z.number(),
  slotsMarkedNa: z.number(),
  progress: EvidenceBoardProgressSchema,
  analysisTriggered: z.boolean(),
  analysisJobId: z.string().optional()
});

// src/focus-inference.ts
var FOCUS_INFERENCE_CONFIDENCE_THRESHOLD = 0.6;
var VALID_LENSES = new Set(Object.values(FocusLens));
function isFocusLens(value) {
  return typeof value === "string" && VALID_LENSES.has(value);
}
function clampConfidence(raw) {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 0;
  if (raw < 0) return 0;
  if (raw > 1) return 1;
  return raw;
}
function parseRationale(raw) {
  if (!Array.isArray(raw)) return [];
  const items = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== "object") continue;
    const { phrase, lens, reason } = entry;
    if (!isFocusLens(lens)) continue;
    if (typeof phrase !== "string" || typeof reason !== "string") continue;
    items.push({ phrase, lens, reason });
  }
  return items;
}
function parseFocusInferenceResponse(raw) {
  const obj = raw !== null && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const confidence = clampConfidence(obj.confidence);
  const rationale = parseRationale(obj.rationale);
  const ordered = [];
  const seen = /* @__PURE__ */ new Set();
  const add = (lens) => {
    if (!seen.has(lens)) {
      seen.add(lens);
      ordered.push(lens);
    }
  };
  const rawSelections = Array.isArray(obj.selections) ? obj.selections : [];
  for (const value of rawSelections) {
    if (isFocusLens(value)) add(value);
  }
  for (const item of rationale) {
    add(item.lens);
  }
  add(FocusLens.DEFENSIBILITY_AI);
  const nothingInferred = seen.size === 1;
  const lowConfidence = confidence < FOCUS_INFERENCE_CONFIDENCE_THRESHOLD || nothingInferred;
  return {
    selections: ordered,
    confidence,
    rationale,
    lowConfidence
  };
}

// src/schemas.ts
import { z as z2 } from "zod";
var UUIDSchema = z2.string().uuid("Invalid UUID format");
var EmailSchema = z2.string().email("Invalid email format");
var PasswordSchema = z2.string().min(
  PASSWORD_MIN_LENGTH,
  `Password must be at least ${PASSWORD_MIN_LENGTH} characters`
).regex(PASSWORD_REGEX, PASSWORD_ERROR_MESSAGE);
var ISODateSchema = z2.string().datetime("Invalid ISO date format");
var AssessmentWorkflowStateSchema = z2.enum([
  AssessmentWorkflowState.COLLECTING_EVIDENCE,
  AssessmentWorkflowState.ANSWERING_QUESTIONS,
  AssessmentWorkflowState.ANSWERS_SUBMITTED,
  AssessmentWorkflowState.REVIEWING_REPORT,
  AssessmentWorkflowState.SHARED,
  AssessmentWorkflowState.FINALIZED
]);
var CompanyStageSchema = z2.enum([
  CompanyStage.PRE_SEED,
  CompanyStage.SEED,
  CompanyStage.SERIES_A,
  CompanyStage.SERIES_B,
  CompanyStage.GROWTH,
  CompanyStage.MATURE
]);
var AssessmentPurposeSchema = z2.enum([
  AssessmentPurpose.FUNDRAISING,
  AssessmentPurpose.M_AND_A,
  AssessmentPurpose.INTERNAL_AUDIT,
  AssessmentPurpose.BOARD_UPDATE
]);
var QuestionPrioritySchema = z2.enum([
  QuestionPriority.HIGH,
  QuestionPriority.MEDIUM,
  QuestionPriority.LOW
]);
var QuestionCategorySchema = z2.enum([
  QuestionCategory.ARCHITECTURE,
  QuestionCategory.SDLC,
  QuestionCategory.DEVOPS_INFRASTRUCTURE,
  QuestionCategory.PRODUCT_MANAGEMENT,
  QuestionCategory.TEAM_ORGANIZATION
]);
var AnalysisDepthSchema = z2.enum([
  AnalysisDepth.HIGH,
  AnalysisDepth.MEDIUM,
  AnalysisDepth.LOW
]);
var ResponseStatusSchema = z2.enum([
  ResponseStatus.UNANSWERED,
  ResponseStatus.ANSWERED,
  ResponseStatus.DRAFT,
  ResponseStatus.REVIEW,
  ResponseStatus.FINAL
]);
var UserRoleSchema = z2.enum([UserRole.ADMIN, UserRole.MEMBER]);
var CreateUserInputSchema = z2.object({
  email: EmailSchema,
  password: PasswordSchema,
  companyName: z2.string().min(1, "Company name is required").max(255)
});
var LoginInputSchema = z2.object({
  email: EmailSchema,
  password: z2.string().min(1, "Password is required")
});
var VerifyEmailInputSchema = z2.object({
  token: z2.string().min(1, "Verification token is required")
});
var RefreshTokenInputSchema = z2.object({
  refreshToken: z2.string().min(1, "Refresh token is required")
});
var CreateAssessmentInputSchema = z2.object({
  title: z2.string().max(255, "Title must not exceed 255 characters").optional(),
  companyName: z2.string().min(1).max(255).optional(),
  companyStage: CompanyStageSchema.optional(),
  assessmentPurpose: AssessmentPurposeSchema,
  specificConcerns: z2.string().max(2e3, "Specific concerns must be less than 2000 characters").optional()
});
var UpdateAssessmentInputSchema = z2.object({
  title: z2.string().max(255, "Title must not exceed 255 characters").nullable().optional(),
  companyName: z2.string().min(1).max(255).optional(),
  companyStage: CompanyStageSchema.optional(),
  assessmentPurpose: AssessmentPurposeSchema.optional(),
  specificConcerns: z2.string().max(2e3).nullable().optional(),
  workflowState: AssessmentWorkflowStateSchema.optional()
});
var AssessmentQuerySchema = z2.object({
  workflowState: AssessmentWorkflowStateSchema.optional(),
  page: z2.coerce.number().int().positive().default(1),
  limit: z2.coerce.number().int().min(1).max(100).default(20)
});
var DocumentUploadInputSchema = z2.object({
  assessmentId: UUIDSchema,
  fileName: z2.string().min(1, "File name is required").max(255),
  fileSize: z2.number().int().positive().max(
    MAX_FILE_SIZE_BYTES,
    `File size must not exceed ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB`
  ),
  fileType: z2.string().min(1, "File type is required"),
  checklistItemId: z2.string().min(1, "Checklist item ID is required")
});
var ConfirmDocumentUploadInputSchema = z2.object({
  documentId: UUIDSchema
});
var TextSubmissionInputSchema = z2.object({
  assessmentId: UUIDSchema,
  documentTypeId: z2.string().min(1, "Document type ID is required"),
  textContent: z2.string().min(1, "Text content is required").max(
    MAX_TEXT_SUBMISSION_LENGTH,
    `Text content must not exceed ${MAX_TEXT_SUBMISSION_LENGTH.toLocaleString()} characters`
  ),
  title: z2.string().max(255, "Title must not exceed 255 characters").optional()
});
var SOURCE_LABEL_MAX = MAX_SOURCE_LABEL_LENGTH;
var DocumentQuerySchema = z2.object({
  assessmentId: UUIDSchema,
  status: z2.string().optional(),
  isRecommended: z2.boolean().optional()
});
var CreateQuestionInputSchema = z2.object({
  assessmentId: UUIDSchema,
  questionText: z2.string().min(10, "Question must be at least 10 characters").max(2e3),
  priority: QuestionPrioritySchema,
  category: QuestionCategorySchema,
  context: z2.string().max(1e3).optional(),
  suggestedApproach: z2.string().max(1e3).optional()
});
var UpdateQuestionInputSchema = z2.object({
  questionText: z2.string().min(10).max(2e3).optional(),
  priority: QuestionPrioritySchema.optional(),
  category: QuestionCategorySchema.optional(),
  context: z2.string().max(1e3).optional(),
  suggestedApproach: z2.string().max(1e3).optional(),
  displayOrder: z2.number().int().positive().optional()
});
var QuestionResponseInputSchema = z2.object({
  responseText: z2.string().min(1, "Response text is required").max(1e4),
  // STA-282: default is `draft`, NOT `answered`. The explicit Submit button on
  // the frontend is the only path that should mark a response complete; any
  // caller omitting `responseStatus` should land safely in the in-progress
  // state instead of silently advancing assessment progress.
  responseStatus: z2.enum([
    ResponseStatus.ANSWERED,
    ResponseStatus.DRAFT,
    ResponseStatus.REVIEW,
    ResponseStatus.FINAL
  ]).default(ResponseStatus.DRAFT)
});
var QuestionQuerySchema = z2.object({
  assessmentId: UUIDSchema,
  priority: QuestionPrioritySchema.optional(),
  category: QuestionCategorySchema.optional(),
  responseStatus: ResponseStatusSchema.optional(),
  search: z2.string().max(255).optional(),
  page: z2.coerce.number().int().positive().default(1),
  limit: z2.coerce.number().int().min(1).max(100).default(50)
});
var ReorderQuestionsInputSchema = z2.object({
  questionIds: z2.array(UUIDSchema).min(1, "At least one question ID required")
});
var AssignQuestionInputSchema = z2.object({
  assignedToUserId: UUIDSchema.nullable(),
  notifyByEmail: z2.boolean().default(true)
});
var AssessmentProgressResponseSchema = z2.object({
  total: z2.number().int().nonnegative(),
  answered: z2.number().int().nonnegative(),
  percentComplete: z2.number().min(0).max(100),
  unassignedCount: z2.number().int().nonnegative(),
  byDimension: z2.array(
    z2.object({
      category: z2.string(),
      total: z2.number().int().nonnegative(),
      answered: z2.number().int().nonnegative(),
      percentComplete: z2.number().min(0).max(100)
    })
  ),
  byMember: z2.array(
    z2.object({
      userId: UUIDSchema,
      email: z2.string().email(),
      displayName: z2.string().nullable(),
      assigned: z2.number().int().nonnegative(),
      answered: z2.number().int().nonnegative(),
      percentComplete: z2.number().min(0).max(100)
    })
  )
});
var OrgMembersResponseSchema = z2.object({
  members: z2.array(
    z2.object({
      userId: UUIDSchema,
      email: z2.string().email(),
      displayName: z2.string().nullable(),
      role: z2.enum(["owner", "admin", "member", "viewer"])
    })
  )
});
var AnalysisQuerySchema = z2.object({
  assessmentId: UUIDSchema
});
var ExportPDFInputSchema = z2.object({
  assessmentId: UUIDSchema,
  includeResponses: z2.boolean().default(false)
});
var PaginationSchema = z2.object({
  page: z2.coerce.number().int().positive().default(1),
  limit: z2.coerce.number().int().min(1).max(100).default(20)
});
var PaginationMetaSchema = z2.object({
  page: z2.number().int().positive(),
  limit: z2.number().int().positive(),
  total: z2.number().int().nonnegative(),
  totalPages: z2.number().int().nonnegative()
});
var APIErrorSchema = z2.object({
  error: z2.object({
    code: z2.string(),
    message: z2.string(),
    details: z2.record(z2.any()).optional()
  })
});
function validateOrThrow(schema, data) {
  const result = schema.safeParse(data);
  if (!result.success) {
    const errors = result.error.errors.map((err) => ({
      field: err.path.join("."),
      message: err.message
    }));
    throw new ValidationError("Validation failed", errors);
  }
  return result.data;
}
var ValidationError = class extends Error {
  constructor(message, errors) {
    super(message);
    this.errors = errors;
    this.name = "ValidationError";
  }
};
function safeParse(schema, data) {
  const result = schema.safeParse(data);
  if (!result.success) {
    return {
      success: false,
      errors: result.error.errors.map((err) => ({
        field: err.path.join("."),
        message: err.message
      }))
    };
  }
  return { success: true, data: result.data };
}

// src/infra-scan.ts
var CloudProvider = {
  AWS: "AWS",
  GCP: "GCP"
};
var CloudConnectionStatus = {
  PENDING: "pending",
  CONNECTED: "connected",
  ERROR: "error",
  REVOKED: "revoked"
};
var InfraScanStatus = {
  PENDING: "pending",
  PROVISIONING: "provisioning",
  VERIFYING: "verifying",
  SCANNING: "scanning",
  INGESTING: "ingesting",
  ANALYZING: "analyzing",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled"
};
var ACTIVE_INFRA_SCAN_STATUSES = /* @__PURE__ */ new Set([
  InfraScanStatus.PENDING,
  InfraScanStatus.PROVISIONING,
  InfraScanStatus.VERIFYING,
  InfraScanStatus.SCANNING,
  InfraScanStatus.INGESTING,
  InfraScanStatus.ANALYZING
]);
var InfraCheckStatus = {
  FAIL: "FAIL",
  PASS: "PASS",
  MANUAL: "MANUAL"
};
var InfraService = {
  // AWS services
  IAM: "iam",
  S3: "s3",
  EC2: "ec2",
  VPC: "vpc",
  RDS: "rds",
  CLOUDTRAIL: "cloudtrail",
  GUARDDUTY: "guardduty",
  KMS: "kms",
  LAMBDA: "lambda",
  ECS: "ecs",
  EKS: "eks",
  SECRETSMANAGER: "secretsmanager",
  CONFIG: "config",
  SNS: "sns",
  SQS: "sqs",
  CLOUDWATCH: "cloudwatch",
  ELASTICACHE: "elasticache",
  DYNAMODB: "dynamodb",
  // GCP services
  COMPUTE: "compute",
  STORAGE: "storage",
  BIGQUERY: "bigquery",
  CLOUD_SQL: "cloudsql"
};
function deriveInfraTrackStatus(connection, latestScan) {
  if (!connection) return "not_started";
  if (connection.status === "pending") return "connecting";
  if (connection.status === "error") return "connecting";
  if (connection.status === "revoked") return "not_started";
  if (!latestScan) return "connected";
  if (ACTIVE_INFRA_SCAN_STATUSES.has(latestScan.status)) return "scanning";
  if (latestScan.status === "completed") return "complete";
  return "connected";
}

// src/errors.ts
function toError(e) {
  if (e instanceof Error) return e;
  if (typeof e === "object" && e !== null && "message" in e) {
    const msg = e.message;
    if (typeof msg === "string") return new Error(msg);
  }
  return new Error(typeof e === "string" ? e : String(e));
}
function toErrorMessage(e) {
  return toError(e).message;
}
function hasProperty(e, key) {
  return typeof e === "object" && e !== null && key in e;
}

// src/mcp.ts
var MCP_SERVER_KEY = "stackverify";
var MCP_ENDPOINT_PATH = "/api/agent/mcp";
function buildMcpEndpointUrl(apiBaseUrl) {
  return `${apiBaseUrl.replace(/\/$/, "")}${MCP_ENDPOINT_PATH}`;
}
function buildMcpClientConfig(mcpUrl) {
  const claudeCode = JSON.stringify(
    {
      mcpServers: {
        [MCP_SERVER_KEY]: {
          type: "http",
          url: mcpUrl
        }
      }
    },
    null,
    2
  );
  const claudeDesktop = JSON.stringify(
    {
      mcpServers: {
        [MCP_SERVER_KEY]: {
          command: "npx",
          args: ["-y", "mcp-remote", mcpUrl]
        }
      }
    },
    null,
    2
  );
  return { claudeCode, claudeDesktop };
}

// src/index.ts
import { z as z3 } from "zod";
export {
  ACCEPTED_FILE_TYPES,
  ACCESS_TOKEN_TTL_MINUTES,
  ACTIVE_CODE_SCAN_STATUSES,
  ACTIVE_INFRA_SCAN_STATUSES,
  AGENT_SUBMISSION_USER_ID,
  ALWAYS_ON_READS,
  ANALYSIS_READS,
  APIErrorSchema,
  AnalysisDepth,
  AnalysisDepthColors,
  AnalysisDepthDescriptions,
  AnalysisDepthLabels,
  AnalysisDepthSchema,
  AnalysisQuerySchema,
  AnalysisRead,
  AssessmentMilestone,
  AssessmentMilestoneLabels,
  AssessmentProgressResponseSchema,
  AssessmentPurpose,
  AssessmentPurposeLabels,
  AssessmentPurposeSchema,
  AssessmentQuerySchema,
  AssessmentWorkflowState,
  AssessmentWorkflowStateDescriptions,
  AssessmentWorkflowStateLabels,
  AssessmentWorkflowStateSchema,
  AssignQuestionInputSchema,
  CODE_UPLOAD_CONTENT_TYPE,
  CODE_UPLOAD_MAX_SIZE_BYTES,
  CODE_UPLOAD_REPORT_ANNOTATION,
  CloudConnectionStatus,
  CloudProvider,
  CodeScanFindingSource,
  CodeScanMode,
  CodeScanStatus,
  CodeSourceType,
  CodeSourceTypeLabels,
  CompanyStage,
  CompanyStageLabels,
  CompanyStageSchema,
  ConfirmDocumentUploadInputSchema,
  CreateAssessmentInputSchema,
  CreateQuestionInputSchema,
  CreateUserInputSchema,
  DEEP_DIVE_QUESTION_BUDGET,
  DEFAULT_INVESTOR_NAME,
  DEFAULT_PAGE_SIZE,
  DEPENDENCY_ECOSYSTEMS,
  DIMENSION_FOCUS_COPY,
  DIMENSION_SLUGS,
  DOCUMENT_EXTENSIONS,
  DOCUMENT_MIME_TYPES,
  DealType,
  DepthTier,
  DocumentQuerySchema,
  DocumentStatus,
  DocumentStatusLabels,
  DocumentUploadInputSchema,
  DocumentsTrackStatus,
  EMAIL_VERIFICATION_TOKEN_TTL_HOURS,
  EMBEDDING_CHUNK_OVERLAP_TOKENS,
  EMBEDDING_CHUNK_SIZE_TOKENS,
  EmailSchema,
  EvidenceBoardContractSummarySchema,
  EvidenceBoardDimensionGroupSchema,
  EvidenceBoardProgressSchema,
  EvidenceBoardSchema,
  EvidenceBoardSubmitResultSchema,
  EvidenceRequestPreviewSchema,
  EvidenceSlotSchema,
  EvidenceSlotState,
  EvidenceSource,
  EvidenceSourceLabels,
  EvidenceTrackId,
  EvidenceTrackLabels,
  EvidenceTrackStatus,
  ExportPDFInputSchema,
  FOCUS_INFERENCE_CONFIDENCE_THRESHOLD,
  FOCUS_LENS_TO_READS,
  FillMethod,
  FindingCategory,
  FindingSeverity,
  FindingSeverityColors,
  FindingSeverityLabels,
  FindingType,
  FocusLens,
  FocusLensLabels,
  IMAGE_EXTENSIONS,
  IMAGE_MIME_TYPES,
  IMPACT_LEVELS,
  INFRA_SCANNING_ENABLED,
  ISODateSchema,
  InfraCheckStatus,
  InfraScanStatus,
  InfraService,
  JobType,
  LIKELIHOOD_COLLAPSE_MAP,
  LIKELIHOOD_LEVELS,
  LLM_MAX_TOKENS_ANALYSIS,
  LLM_MAX_TOKENS_QUESTIONS,
  LLM_MODEL_CLAUDE,
  LLM_MODEL_OPENAI_EMBEDDINGS,
  LLM_TEMPERATURE_ANALYSIS,
  LLM_TEMPERATURE_QUESTIONS,
  LOGIN_RATE_LIMIT_ATTEMPTS,
  LOGIN_RATE_LIMIT_WINDOW_MINUTES,
  LoginInputSchema,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  MAX_PAGE_SIZE,
  MAX_QUESTIONS_PER_ASSESSMENT,
  MAX_SOURCE_LABEL_LENGTH,
  MAX_TEXT_SUBMISSION_LENGTH,
  MAX_VISION_IMAGE_DIMENSION,
  MCP_ENDPOINT_PATH,
  MCP_SERVER_KEY,
  MILESTONE_SEQUENCE,
  MIN_QUESTIONS_PER_ASSESSMENT,
  ManagementNoteStatus,
  OrgMembersResponseSchema,
  PASSWORD_ERROR_MESSAGE,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REGEX,
  PORTFOLIO_LINK_TYPES,
  PRESIGNED_URL_TTL_MINUTES,
  PaginationMetaSchema,
  PaginationSchema,
  PasswordSchema,
  QUESTION_DEPTH_BUDGETS,
  QUESTION_DIMENSION_READS,
  QUESTION_PRIORITY_DISTRIBUTION,
  QuestionAssignmentStatus,
  QuestionAssignmentStatusLabels,
  QuestionCategories,
  QuestionCategory,
  QuestionCategorySchema,
  QuestionCategoryShortLabels,
  QuestionPriority,
  QuestionPriorityColors,
  QuestionPriorityLabels,
  QuestionPrioritySchema,
  QuestionQuerySchema,
  QuestionResponseInputSchema,
  READ_DEEP_DEPENDENCIES,
  REFRESH_TOKEN_TTL_DAYS,
  REPORT_FROZEN_STATES,
  REQUEST_URGENCY_EXPIRY_DAYS,
  RISK_MATRIX_ZONE_HEX,
  RISK_MATRIX_ZONE_STYLES,
  RISK_SEVERITY,
  RefreshTokenInputSchema,
  ReorderQuestionsInputSchema,
  ReportType,
  RequestStatus,
  RequestStatusLabels,
  RequestUrgency,
  Requiredness,
  ResponseStatus,
  ResponseStatusColors,
  ResponseStatusLabels,
  ResponseStatusSchema,
  RiskImpact,
  RiskImpactLabels,
  RiskLikelihood4,
  RiskLikelihood4Labels,
  SOURCE_LABEL_MAX,
  STAGE_ORDER,
  STANDARD_QUESTION_BUDGET,
  SUPPORTED_FILE_EXTENSIONS,
  Stage,
  StageLabels,
  TERMINAL_CODE_SCAN_STATUSES,
  TextSubmissionInputSchema,
  UUIDSchema,
  UpdateAssessmentInputSchema,
  UpdateQuestionInputSchema,
  UploadedCodeSourceStatus,
  UserRole,
  UserRoleLabels,
  UserRoleSchema,
  VISION_COMPATIBLE_IMAGE_EXTENSIONS,
  ValidationError,
  VerifyEmailInputSchema,
  assembleBoardDimensions,
  assessmentStateToRequestProgress,
  buildActionItems,
  buildEvidenceRequest,
  buildMcpClientConfig,
  buildMcpEndpointUrl,
  buildPortfolioPatterns,
  buildRiskDistribution,
  buildRiskMatrix,
  computeBoardProgress,
  countActiveDeals,
  deriveDepth,
  deriveInfraTrackStatus,
  deriveMilestone,
  deriveQuestionPlan,
  deriveTrackStatuses,
  emptyFindingCounts,
  emptyFindingCountsByStatus,
  getCellZone,
  getQuestionAssignmentStatus,
  hasProperty,
  isActiveCodeScanStatus,
  isActiveDeal,
  isInvestorVisible,
  isReportFrozen,
  isStaleAssessment,
  isTerminalCodeScanStatus,
  isTerminalRequest,
  mapLikelihoodToFour,
  milestoneIndex,
  parseFocusInferenceResponse,
  safeParse,
  stageAtLeast,
  toError,
  toErrorMessage,
  toStringArray,
  validateOrThrow,
  z3 as z
};
